/**
 * The animations of the gameplay page: small scenes of the real game (the same simulation, sprites and effects as a
 * match, in a small world under the sandbox's rules), scripted and played in a loop. Display only: nothing is sent.
 * One clock for every stage; a stage only runs while it is on screen (and the page is shown).
 */
import { createWorld, paintRect, Physique, type World } from '@invaders/sim';
import {
  createGameState, createUnit, placeOnGround, processDeaths, tick, FP, UNIT_W,
  type Command, type CommandInput, type GameState, type Unit,
} from '@invaders/game';
import { Camera } from '../shared/camera';
import { ViewRenderer } from '../shared/view-renderer';
import { Effects } from '../shared/effects';
import { UnitAnimator } from '../shared/sprites';
import { themeFor } from '../shared/themes';
import { drawBatteries, drawMachines, drawChests, drawImmersion, drawMines, drawProjectiles, drawSteles, drawSurges, drawUnit } from '../shared/draw-units';
import { startFixedLoop } from '../shared/fixed-loop';

/** The world of a scene, in cells (the map of a match is 1024 × 512). */
export const STAGE_W = 256;
export const STAGE_H = 112;
/** The ground of the plain scenes: its top row. */
export const FLOOR = 88;

/** What a scene's script gets: the state, and a way to send commands (applied at the next tick). */
export interface SceneCtx {
  readonly state: GameState;
  send(c: CommandInput): void;
  /** A unit of `kind` for `player` with its feet on the ground under column x (its left side), dropped from row `top`. */
  unit(player: number, kind: number, x: number, top?: number): Unit;
  /** Changes made between ticks (damage, legacies, freezing): their deaths are handled and their events shown. */
  direct(f: () => void): void;
  /** A caption under the scene (the step of a turn, a legacy's turn...). */
  caption(text: string): void;
}

export interface Scene {
  /** Length of the loop, in ticks (60 per second). */
  readonly ticks: number;
  /** A bigger world than STAGE_W × STAGE_H (the same shape), shown smaller: for the weapons that need room. */
  readonly w?: number;
  readonly h?: number;
  /** The look of the sky and the terrain (a map's theme). */
  readonly theme?: string;
  /** The world before the first tick: the plain ground by default. */
  terrain?(world: World): void;
  setup(c: SceneCtx): void;
  /** Called before each tick `t` (0 at the first). */
  at?(t: number, c: SceneCtx): void;
}

/** The plain ground: rock under a few rows of sand, its top at `floor` (FLOOR scaled to the world). */
export function plainGround(world: World, floor = Math.round((FLOOR * world.physique.h) / STAGE_H)): void {
  const { w, h } = world.physique;
  paintRect(world, 0, floor, w - 1, h - 1, Physique.ROCK);
  paintRect(world, 0, floor, w - 1, floor + 3, Physique.SAND);
}

const stages: Stage[] = [];

/** A canvas that plays one scene after another (`play`), each in a loop, while it is on screen. */
export class Stage {
  private state: GameState | null = null;
  private scene: Scene | null = null;
  private t = 0;
  private pending: Command[] = [];
  private visible = false;
  private readonly ctx: CanvasRenderingContext2D;
  private cam = new Camera(STAGE_W, STAGE_H);
  private readonly renderer = new ViewRenderer();
  private effects = new Effects();
  private animator = new UnitAnimator();
  private sky: CanvasGradient | null = null;
  private skyFor = '';
  /** Called at the end of each loop of the scene (the panels move on to the next item). */
  onLoop: (() => void) | null = null;
  onCaption: ((text: string) => void) | null = null;

  constructor(readonly canvas: HTMLCanvasElement) {
    this.ctx = canvas.getContext('2d')!;
    new IntersectionObserver((entries) => { this.visible = entries.some((e) => e.isIntersecting); }, { rootMargin: '80px' }).observe(canvas);
    stages.push(this);
    if (stages.length === 1) startFixedLoop(() => { for (const s of stages) s.update(); }, () => { for (const s of stages) s.render(); }, () => !document.hidden);
  }

  play(scene: Scene): void {
    this.scene = scene;
    this.restart();
  }

  private restart(): void {
    const scene = this.scene!;
    const world = createWorld(scene.w ?? STAGE_W, scene.h ?? STAGE_H, { seed: 7 });
    if (scene.terrain) scene.terrain(world);
    else plainGround(world);
    this.cam = new Camera(world.physique.w, world.physique.h);
    this.state = createGameState(world);
    this.t = 0;
    this.pending = [];
    this.effects = new Effects();
    this.animator = new UnitAnimator();
    const theme = themeFor(scene.theme ?? 'island');
    this.renderer.setTheme(theme);
    if (this.skyFor !== (scene.theme ?? 'island')) { this.sky = null; this.skyFor = scene.theme ?? 'island'; }
    this.onCaption?.('');
    scene.setup(this.context());
  }

  private context(): SceneCtx {
    const state = this.state!;
    return {
      state,
      send: (c) => { this.pending.push({ ...c, tick: state.tick } as Command); },
      unit: (player, kind, x, top = 0) => {
        const u = createUnit(state.units.length, player, kind, x, top);
        placeOnGround(state.world, u);
        state.units.push(u);
        return u;
      },
      direct: (f) => {
        const before = state.events.splice(0);
        f();
        processDeaths(state);
        this.show(state.events);
        state.events.unshift(...before);
      },
      caption: (text) => { this.onCaption?.(text); },
    };
  }

  private show(events: GameState['events']): void {
    const state = this.state!;
    this.effects.onEvents(events, state.world, (id) => { const u = state.units[id]; return u ? [u.x / FP + UNIT_W / 2, u.y / FP - 2] : null; });
    this.animator.onEvents(state, events, performance.now());
  }

  private update(): void {
    if (!this.visible || !this.scene || !this.state) return;
    if (this.t >= this.scene.ticks) {
      this.onLoop?.();
      // onLoop may have started another scene.
      if (this.t >= this.scene.ticks) this.restart();
    }
    const state = this.state;
    this.scene.at?.(this.t, this.context());
    const batch = this.pending;
    this.pending = [];
    for (const c of batch) c.tick = state.tick;
    tick(state, batch);
    this.show(state.events);
    this.t++;
  }

  private render(): void {
    if (!this.visible || !this.state) return;
    const canvas = this.canvas;
    const dpr = window.devicePixelRatio || 1;
    const w = Math.round(canvas.clientWidth * dpr);
    const h = Math.round((w * STAGE_H) / STAGE_W);
    const sw = this.state.world.physique.w;
    const sh = this.state.world.physique.h;
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
      this.sky = null;
    }
    this.cam.resize(w, h, w / sw);
    this.cam.centerOn(sw / 2, sh / 2);
    const ctx = this.ctx;
    const now = performance.now();
    this.effects.update(1 / 60);
    if (!this.sky) {
      this.sky = ctx.createLinearGradient(0, 0, 0, h);
      for (const [at, color] of themeFor(this.skyFor).sky) this.sky.addColorStop(Math.min(1, at * 1.6), color);
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = this.sky;
    ctx.fillRect(0, 0, w, h);
    const state = this.state;
    const cam = this.cam;
    this.renderer.draw(ctx, state.world, cam);
    this.effects.drawBack(ctx, cam);
    drawSteles(ctx, cam, state);
    drawChests(ctx, cam, state);
    drawMines(ctx, cam, state, now);
    drawBatteries(ctx, cam, state, now);
    drawMachines(ctx, cam, state, now);
    for (const u of state.units) drawUnit(ctx, cam, u, this.animator, now);
    drawImmersion(ctx, cam, state);
    this.animator.drawDeaths(ctx, cam, now);
    drawProjectiles(ctx, cam, state);
    drawSurges(ctx, cam, state, now);
    this.effects.jets(state);
    this.effects.drawFront(ctx, cam);
  }
}

/**
 * A list of items (rows of a table, steps of a turn) and the stage that shows them: one item after the other, each
 * played `loops` times, until one is picked (click, Enter): then that one, in a loop.
 */
export function demoPanel(stage: Stage, items: { el: HTMLElement; scene: Scene }[], captionEl?: HTMLElement, loops = 1): void {
  let current = -1;
  let played = 0;
  let pinned = false;
  const pick = (i: number) => {
    if (current >= 0) items[current].el.classList.remove('playing');
    current = i;
    played = 0;
    items[i].el.classList.add('playing');
    stage.play(items[i].scene);
  };
  stage.onLoop = () => {
    if (pinned || ++played < loops) return;
    pick((current + 1) % items.length);
  };
  if (captionEl) stage.onCaption = (text) => { captionEl.textContent = text; };
  items.forEach((it, i) => {
    it.el.tabIndex = 0;
    it.el.addEventListener('click', () => { pinned = true; pick(i); });
    it.el.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pinned = true; pick(i); } });
  });
  pick(0);
}
